import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Stack, Button, Modal, Box, Typography, Paper, LinearProgress, Alert, Chip, Checkbox, CircularProgress,
    Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import LoadingComponent from "../../components/LoadingComponent";
import LaserMarkPanel from "../../components/LaserMarkPanel";
import MarkingTimeChip from "../../components/MarkingTimeChip";
import CameraCapturePanel from "../../components/CameraCapturePanel";
import FinalizeDialog from "../../components/FinalizeDialog";
import { naturalToDisplayedImageCoords, displayedToNaturalImageCoords } from "../../helper/imageOverlayCoordinates";
import {
    IMAGE_SELECTION, SELECTION_KINDS, selectionLockedRoute, selectionResumeRoute, selectionSidebar,
} from "../../helper/selectionFlow";
import { useSelectionParams } from "../../helper/entityRoutes";
import { cardSx } from "./ColorSelectionWidthGroups";
import { PickChip } from "./ColorSelectionPrimaries";

const STATUS_LABELS = { PREPARING: "Preparing plates", FAILED: "Failed", MARKING: "Marking", MEASURED: "Measured" };
const POLL_MS = 3000;
const buttonSx = { textTransform: "none", borderRadius: 2, fontWeight: 600, boxShadow: "none" };

const fmt = (value, digits = 2) => (value == null ? "—" : Number(value).toFixed(digits));
/** "Solid primaries", "Mix grid 1" (rounds before the random grids: "Pairs 1"). */
const setLabel = (key) => {
    if (key === "solids") return "Solid primaries";
    const [kind, index] = key.split("_");
    return `${kind === "mix" ? "Mix grid" : "Pairs"} ${Number(index) + 1}`;
};
const primaryLabel = (p) => (p.role === "white" ? "White" : p.role === "black" ? "Black" : String(p.index + 1));
const primaryName = (p) => (p.role === "chromatic" ? `Primary ${p.index + 1}` : primaryLabel(p));
const emptySlot = () => ({ preview: "", scalingFactors: null, cornerPoints: [], busy: false, message: "", savingCorners: false });

function pngObjectUrl(latin1) {
    const bytes = new Uint8Array(latin1.split("").map((char) => char.charCodeAt(0)));
    return URL.createObjectURL(new Blob([bytes], { type: "image/png" }));
}

function toggled(list, id) {
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

// Image selection, predictability test (the colour-selection script's
// stages 12-14): the solid primaries plus two 10 × 10 grids of the primaries'
// pairs at random mix ratios, each grid with its corner crosses. The measured
// patches are compared with the Yule-Nielsen mix of the measured solids at
// the ratio each patch's halftone marked. After the measurement the
// iteration ends: primaries taken out leave the selection for good and it
// restarts at the width groups (next iteration), or the primaries are
// finalized as the palette. Both are confirmed first.
export default function ColorSelectionPredictability() {
    const navigate = useNavigate();
    const { selectionId, stepRoute } = useSelectionParams();
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [round, setRound] = useState(null);
    const [markPlate, setMarkPlate] = useState(1);
    const [activeSet, setActiveSet] = useState(null);
    const [slots, setSlots] = useState({});
    const slotsRef = useRef(slots);
    slotsRef.current = slots;
    const [norm, setNorm] = useState({ busy: false, preview: "" });
    const [takeOut, setTakeOut] = useState([]);
    const [measuring, setMeasuring] = useState(false);
    const [preparing, setPreparing] = useState(false);
    const [finalizeOpen, setFinalizeOpen] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const [takeOutOpen, setTakeOutOpen] = useState(false);
    const [takingOut, setTakingOut] = useState(false);
    const [modalSet, setModalSet] = useState(null);
    const [autoCornersBusy, setAutoCornersBusy] = useState(false);
    const [, setLayoutTick] = useState(0);
    const slotImageRefs = useRef({});
    const modalImageRef = useRef(null);
    const roundRequest = useRef(0);
    const previewRequested = useRef({});
    const normRequested = useRef(null);

    const updateSlot = (key, partial) => {
        setSlots((prev) => ({ ...prev, [key]: { ...(prev[key] ?? emptySlot()), ...partial } }));
    };

    // Uploads run in parallel and each re-reads the round; an older answer
    // that arrives after a newer one is ignored.
    const refresh = async () => {
        const id = roundRequest.current + 1;
        roundRequest.current = id;
        try {
            const response = await axios.post("api/selection/image/predictability", { selectionId });
            if (id === roundRequest.current) setRound(response.data.predictability);
        } catch (error) {
            console.error("Error loading the predictability round:", error);
            setErrorMessage(error?.response?.data?.error || "Loading the predictability round failed.");
        }
    };

    useEffect(() => {
        const load = async () => {
            try {
                const selectionResponse = await axios.get(`api/color/selection/${selectionId}/`);
                const status = (selectionResponse.data.status || "").toUpperCase();
                if (status !== "PREDICTABILITY") {
                    navigate(
                        selectionLockedRoute(selectionId, status, IMAGE_SELECTION)
                            || selectionResumeRoute(selectionId, status, IMAGE_SELECTION),
                        { replace: true },
                    );
                    return;
                }
                await refresh();
            } catch (error) {
                console.error("Error loading the selection:", error);
                setErrorMessage(error?.response?.data?.error || "Loading the predictability round failed.");
            } finally {
                setLoading(false);
            }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const roundNumber = round?.number;
    const roundStatus = round?.status;
    // A new round starts with empty photo slots and nothing checked.
    useEffect(() => {
        setSlots({});
        previewRequested.current = {};
        normRequested.current = null;
        setNorm({ busy: false, preview: "" });
        setTakeOut([]);
        setActiveSet(null);
        setMarkPlate(1);
    }, [roundNumber]);

    // The plates are prepared in the background: poll until they are ready.
    useEffect(() => {
        if (roundStatus !== "PREPARING") return undefined;
        const timer = setInterval(refresh, POLL_MS);
        return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roundStatus, roundNumber]);

    useEffect(() => {
        if (!round?.normalization || normRequested.current === roundNumber) return;
        normRequested.current = roundNumber;
        axios.post("api/selection/image/predictability/normalization/preview", { selectionId }, { timeout: 600000 })
            .then((response) => {
                if (response.data?.image != null) setNorm((prev) => ({ ...prev, preview: pngObjectUrl(response.data.image) }));
            })
            .catch((error) => console.error("Error loading the normalization preview:", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [round?.normalization, roundNumber]);

    const sets = round?.sets ?? [];
    const samples = round?.samples ?? {};
    const active = sets.find((s) => s.key === activeSet) ?? sets.find((s) => !s.capture.complete) ?? sets[0] ?? null;

    const applyPreview = (key, data) => {
        const xScale = parseFloat(data?.scaling_factors?.x_scale ?? 1);
        const yScale = parseFloat(data?.scaling_factors?.y_scale ?? 1);
        updateSlot(key, {
            preview: pngObjectUrl(data?.image || ""),
            scalingFactors: { xScale, yScale },
            cornerPoints: (data?.cornerPoints || []).map((point) => ({ x_a: point[0] / xScale, y_a: point[1] / yScale })),
        });
    };

    const loadStoredPreview = async (key) => {
        updateSlot(key, { busy: true, message: "Loading the stored photo…" });
        try {
            const response = await axios.post("api/selection/image/predictability/photo/preview", { selectionId, set: key }, { timeout: 600000 });
            applyPreview(key, response.data);
        } catch (error) {
            console.error("Error loading the stored photo:", error);
            setErrorMessage(error?.response?.data?.error || "Loading the stored photo failed.");
            previewRequested.current[key] = false;
        } finally {
            updateSlot(key, { busy: false, message: "" });
        }
    };

    // The slot shows one set: fetch its stored photo when it comes into view.
    useEffect(() => {
        if (!active || !active.capture.photo || previewRequested.current[active.key]) return;
        const slot = slotsRef.current[active.key];
        if (slot?.preview || slot?.busy) return;
        previewRequested.current[active.key] = true;
        loadStoredPreview(active.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active?.key, active?.capture.photo, roundNumber]);

    const processSetFile = async (key, file) => {
        if (!file) return;
        updateSlot(key, { busy: true, message: "Uploading, storing and detecting corner points…", cornerPoints: [] });
        setErrorMessage(null);
        try {
            const formData = new FormData();
            formData.append("selectionId", selectionId);
            formData.append("set", key);
            formData.append("file", file);
            const response = await axios.post("api/selection/image/predictability/photo", formData, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 900000,
            });
            applyPreview(key, response.data);
            previewRequested.current[key] = true;
            const next = sets.find((s) => s.key !== key && !s.capture.photo);
            if (response.data?.capture?.complete && next) setActiveSet(next.key);
            refresh();
        } catch (error) {
            console.error("Error storing the photo:", error);
            setErrorMessage(error?.response?.data?.error || "Storing the photo failed.");
        } finally {
            updateSlot(key, { busy: false, message: "" });
        }
    };

    const processNormFile = async (file) => {
        if (!file) return;
        setNorm((prev) => ({ ...prev, busy: true }));
        setErrorMessage(null);
        try {
            const formData = new FormData();
            formData.append("selectionId", selectionId);
            formData.append("file", file);
            const response = await axios.post("api/selection/image/predictability/normalization", formData, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 600000,
            });
            normRequested.current = roundNumber;
            if (response.data?.image != null) setNorm((prev) => ({ ...prev, preview: pngObjectUrl(response.data.image) }));
            refresh();
        } catch (error) {
            console.error("Error storing the normalization image:", error);
            setErrorMessage(error?.response?.data?.error || "Storing the normalization image failed.");
        } finally {
            setNorm((prev) => ({ ...prev, busy: false }));
        }
    };

    // Corner points travel on their own (raw pixels).
    const saveCorners = async (key, options = {}) => {
        const slot = slotsRef.current[key];
        if (!slot) return;
        const { xScale = 1, yScale = 1 } = slot.scalingFactors || {};
        const payload = {
            selectionId, set: key,
            ...(options.detect
                ? { detect: true }
                : { cornerPoints: slot.cornerPoints.map((p) => [p.x_a * xScale, p.y_a * yScale]) }),
        };
        updateSlot(key, { savingCorners: true });
        try {
            const response = await axios.post("api/selection/image/predictability/corners", payload, { timeout: 600000 });
            updateSlot(key, {
                cornerPoints: (response.data.cornerPoints || []).map((point) => ({ x_a: point[0] / xScale, y_a: point[1] / yScale })),
            });
            refresh();
        } catch (error) {
            console.error("Error saving the corner points:", error);
            setErrorMessage(error?.response?.data?.error || "Saving the corner points failed.");
        } finally {
            updateSlot(key, { savingCorners: false });
        }
    };

    const fetchPlate = async (plate) => {
        const response = await axios.post(
            "api/selection/image/predictability/marking/file",
            { selectionId, plate },
            { responseType: "blob" },
        );
        return { blob: response.data, filename: `predictability_round_${round.number}_plate_${plate}.json` };
    };

    const downloadPlate = async (plate) => {
        setErrorMessage(null);
        try {
            const { blob, filename } = await fetchPlate(plate);
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading the marking file:", error);
            setErrorMessage("Downloading the marking file failed.");
        }
    };

    const measure = async () => {
        setMeasuring(true);
        setErrorMessage(null);
        try {
            const response = await axios.post("api/selection/image/predictability/measure", { selectionId }, { timeout: 1800000 });
            setRound(response.data.predictability);
        } catch (error) {
            console.error("Error measuring the predictability:", error);
            setErrorMessage(error?.response?.data?.error || "The predictability measurement failed.");
        } finally {
            setMeasuring(false);
        }
    };

    const prepareAgain = async () => {
        setPreparing(true);
        setErrorMessage(null);
        try {
            const response = await axios.post("api/selection/image/predictability/prepare", { selectionId });
            setRound(response.data.predictability);
        } catch (error) {
            console.error("Error preparing the plates again:", error);
            setErrorMessage(error?.response?.data?.error || "Preparing the plates failed.");
        } finally {
            setPreparing(false);
        }
    };

    // Taken out for good: the next iteration starts at the width groups.
    const confirmTakeOut = async () => {
        setTakingOut(true);
        try {
            await axios.post("api/selection/image/predictability/takeout", { selectionId, sampleIds: takeOut });
            setTakingOut(false);
            setTakeOutOpen(false);
            navigate(stepRoute("widthgroups", IMAGE_SELECTION));
        } catch (error) {
            console.error("Error taking the primaries out:", error);
            setTakingOut(false);
            setTakeOutOpen(false);
            setErrorMessage(error?.response?.data?.error || "Taking the primaries out failed.");
        }
    };

    const finalize = async () => {
        setFinalizing(true);
        try {
            await axios.post("api/selection/image/predictability/finalize", { selectionId });
            setFinalizing(false);
            setFinalizeOpen(false);
            navigate(stepRoute("overview", IMAGE_SELECTION));
        } catch (error) {
            console.error("Error finalizing the primaries:", error);
            setFinalizing(false);
            setFinalizeOpen(false);
            setErrorMessage(error?.response?.data?.error || "Finalizing the primaries failed.");
        }
    };

    // ---- corner modal ----
    const modalSlot = modalSet != null ? slots[modalSet] : null;
    const handleModalImageClick = (event) => {
        const image = modalImageRef.current;
        if (!image || modalSet == null) return;
        const rect = image.getBoundingClientRect();
        const { x: x_a, y: y_a } = displayedToNaturalImageCoords(image, event.clientX - rect.left, event.clientY - rect.top);
        const previous = slotsRef.current[modalSet]?.cornerPoints ?? [];
        if (previous.length >= 4) return;
        updateSlot(modalSet, { cornerPoints: [...previous, { x_a, y_a }] });
    };
    const detectCorners = async () => {
        if (modalSet == null) return;
        setAutoCornersBusy(true);
        try {
            await saveCorners(modalSet, { detect: true });
        } finally {
            setAutoCornersBusy(false);
        }
    };
    const closeModal = async () => {
        const key = modalSet;
        setModalSet(null);
        if (key) await saveCorners(key);
    };
    const cornerDot = (coordinate, imgEl, key) => {
        const pos = naturalToDisplayedImageCoords(imgEl, coordinate.x_a, coordinate.y_a);
        return (
            <Box
                key={key}
                sx={{
                    position: "absolute", top: pos.y - 5, left: pos.x - 5, width: 10, height: 10,
                    borderRadius: "50%", bgcolor: "#f44336", border: "1.5px solid #ffffff", pointerEvents: "none",
                }}
            />
        );
    };

    const uploadButton = (label, disabled, onFile) => (
        <Button component="label" variant="outlined" size="small" disableElevation disabled={disabled} startIcon={<UploadFileIcon />}>
            {label}
            <input hidden accept=".nef, .arw" type="file" disabled={disabled} onChange={(e) => onFile(e.target.files?.[0])} />
        </Button>
    );

    const renderSlot = (spec) => {
        const key = spec.key;
        const slot = slots[key] ?? emptySlot();
        const stored = spec.capture.photo;
        const cornerCount = slot.preview ? slot.cornerPoints.length : (spec.capture.corner_points?.length ?? 0);
        const disabled = slot.busy || measuring;
        return (
            <Stack spacing={2} sx={{ pt: 2.5, mt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                        {`${setLabel(key)}${spec.label ? ` · ${spec.label}` : ""} · plate ${spec.plate}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {`${spec.grid_size[0]} × ${spec.grid_size[1]} patches`}
                    </Typography>
                    {spec.capture.complete && <Chip size="small" color="success" label="Uploaded" />}
                </Stack>
                <CameraCapturePanel
                    label={stored ? "Replace photo" : "Capture photo"}
                    disabled={disabled}
                    onCaptured={(file) => processSetFile(key, file)}
                    manualUpload={uploadButton(stored ? "Replace by manual upload" : "Manual upload", disabled, (file) => processSetFile(key, file))}
                />
                <Box
                    role={slot.preview && !slot.busy ? "button" : undefined}
                    tabIndex={slot.preview && !slot.busy ? 0 : undefined}
                    aria-label={slot.preview && !slot.busy ? "Open full screen to adjust corner points" : undefined}
                    onClick={slot.preview && !slot.busy ? () => setModalSet(key) : undefined}
                    sx={{
                        minWidth: 0, maxWidth: 440, aspectRatio: "1", position: "relative", borderRadius: 2, overflow: "hidden",
                        bgcolor: slot.preview ? "grey.100" : "grey.50", border: "1px dashed",
                        borderColor: slot.preview ? "divider" : "text.disabled",
                        ...(slot.preview && !slot.busy ? { cursor: "pointer", "&:hover": { borderColor: "primary.main", borderStyle: "solid" } } : {}),
                    }}
                >
                    {slot.busy && (
                        <Box sx={{ position: "absolute", inset: 0, zIndex: 4, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, px: 2, bgcolor: "rgba(255, 255, 255, 0.88)" }}>
                            <Typography variant="body2" color="text.secondary" align="center">{slot.message || "Processing…"}</Typography>
                            <LinearProgress sx={{ width: "min(90%, 280px)" }} />
                        </Box>
                    )}
                    {slot.preview ? (
                        <Box sx={{ position: "absolute", inset: 0 }}>
                            <img
                                ref={(el) => { slotImageRefs.current[key] = el; }}
                                src={slot.preview}
                                alt={`Set preview ${setLabel(key)}`}
                                style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                                onLoad={() => setLayoutTick((t) => t + 1)}
                            />
                            {slot.cornerPoints.map((coordinate, i) => cornerDot(coordinate, slotImageRefs.current[key], i))}
                        </Box>
                    ) : (
                        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography variant="caption" color="text.disabled" sx={{ px: 1.5, textAlign: "center" }}>
                                {stored ? "Photo stored on the server" : "Preview appears here"}
                            </Typography>
                        </Box>
                    )}
                </Box>
                {slot.savingCorners ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} thickness={5} aria-label="Saving corner points" />
                        <Typography variant="caption" color="text.secondary">Saving corner points…</Typography>
                    </Stack>
                ) : stored && cornerCount !== 4 ? (
                    <Typography variant="caption" color="text.secondary">
                        {`${cornerCount} of 4 corner points set — open the preview to place them`}
                    </Typography>
                ) : null}
            </Stack>
        );
    };

    const results = round?.status === "MEASURED" ? round.results : null;
    const byPrimary = Object.fromEntries((results?.by_primary ?? []).map((row) => [row.primary, row]));
    const solids = Object.fromEntries((results?.solids ?? []).map((row) => [row.primary, row]));
    const primaries = round?.primaries ?? [];
    const pairLabel = (row) => `${primaryLabel(primaries[row.a])} + ${primaryLabel(primaries[row.b])}`;
    const worstPairs = (results?.by_pair ?? []).slice().sort((x, y) => y.max - x.max).slice(0, 10);
    const excluded = round?.excluded ?? [];
    const iteration = round?.iteration ?? round?.number ?? 1;
    const history = round?.iterations?.history ?? [];
    const layout = round?.layout ?? {};
    const plateCount = round?.plates?.length ?? 0;
    const platesText = `${plateCount} plate${plateCount === 1 ? "" : "s"}`;
    const markingText = layout.ratios
        ? `${sets.length} sets on ${platesText}: the solid primaries, then every pair at ${layout.ratios.map((r) => Math.round(r * 100)).join(" / ")} % mix.`
        : `${sets.length} sets on ${platesText}: the solid primaries, then ${layout.mix_grid_count} grids of ${(layout.mix_grid_size || []).join(" × ")} patches at random mix ratios, ${Number(layout.cell_mm)} mm patches.`;
    const ratioLabel = (key) => (results?.ratio_bin
        ? `${Math.round(Number(key) * 100)}–${Math.round((Number(key) + results.ratio_bin) * 100)} %`
        : `${Math.round(Number(key) * 100)} / ${Math.round((1 - Number(key)) * 100)} %`);
    const labelledSets = sets.map((s) => s.label).filter(Boolean);
    const takeOutNames = primaries.filter((p) => takeOut.includes(p.sample_id)).map(primaryName);
    const platesReady = roundStatus === "MARKING" || roundStatus === "MEASURED";
    const missingSets = sets.filter((s) => !s.capture.complete).length;
    const canMeasure = roundStatus === "MARKING" && Boolean(round?.ready_to_measure);
    const measured = roundStatus === "MEASURED";
    const listRoute = SELECTION_KINDS.image.listRoute;

    const simpleTable = (label, head, rows) => (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="small" aria-label={label}>
                <TableHead>
                    <TableRow>{head.map(([title, align]) => <TableCell key={title} align={align}>{title}</TableCell>)}</TableRow>
                </TableHead>
                <TableBody>{rows}</TableBody>
            </Table>
        </Box>
    );
    const numberCell = (value) => <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{value}</TableCell>;

    return (
        <>
            <Box sx={{ width: "100%", maxWidth: "100%", mt: "2%", mb: 4, boxSizing: "border-box" }}>
                <ExplorationPageShell
                    title="Predictability Test"
                    subtitle="The primaries' pairs at random mix ratios, compared with the Yule-Nielsen prediction from the measured solids."
                    noPaper
                    stepSidebar={selectionSidebar("predictability", round ? primaries.length : null, IMAGE_SELECTION, round?.iterations)}
                    contentBoxSx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", px: 0, py: { xs: 2, md: 3 } }}
                >
                    <Stack spacing={3} sx={{ width: "100%", minWidth: 0, mt: 1 }}>
                        {errorMessage && <Alert severity="error" sx={{ borderRadius: 2 }}>{errorMessage}</Alert>}
                        {loading ? <LoadingComponent /> : round && (
                            <>
                                <Stack spacing={1.5}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ mr: 1 }}>{`Iteration ${iteration}`}</Typography>
                                        <Chip
                                            size="small"
                                            color={roundStatus === "FAILED" ? "error" : measured ? (results?.verdict?.predictable ? "success" : "warning") : "primary"}
                                            label={STATUS_LABELS[roundStatus] || roundStatus}
                                        />
                                        <Chip size="small" variant="outlined" label={`${primaries.length} primaries`} />
                                        {round.cell_width_mm != null && (
                                            <Chip size="small" variant="outlined" label={`${Math.round(round.cell_width_mm * 1000)} µm cell`} />
                                        )}
                                        {excluded.length > 0 && (
                                            <Chip size="small" variant="outlined" label={`${excluded.length} taken out earlier`} />
                                        )}
                                    </Stack>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {primaries.map((p) => samples[p.sample_id] && (
                                            <PickChip key={p.sample_id} sample={samples[p.sample_id]} label={primaryLabel(p)} />
                                        ))}
                                    </Stack>
                                </Stack>

                                {roundStatus === "PREPARING" && (
                                    <Paper elevation={0} sx={cardSx}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {`Preparing the plates of iteration ${iteration}…`}
                                        </Typography>
                                        <LinearProgress sx={{ maxWidth: 420 }} />
                                    </Paper>
                                )}

                                {roundStatus === "FAILED" && (
                                    <Alert
                                        severity="error" sx={{ borderRadius: 2 }}
                                        action={(
                                            <Button size="small" disabled={preparing} onClick={prepareAgain} sx={{ textTransform: "none" }}>
                                                Prepare again
                                            </Button>
                                        )}
                                    >
                                        {round.error || "Preparing the plates failed."}
                                    </Alert>
                                )}

                                {platesReady && (
                                    <Paper elevation={0} sx={cardSx}>
                                        <Typography variant="subtitle1" fontWeight={700}>Marking</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {markingText}
                                        </Typography>
                                        {labelledSets.length > 0 && (
                                            <Typography variant="body2" color="text.secondary">
                                                {`Marked as ${labelledSets.join(" / ")} in the next free slots — the plate is filled iteration by iteration.`}
                                            </Typography>
                                        )}
                                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                                            {round.plates.map(({ plate }) => (
                                                <Button key={plate} variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => downloadPlate(plate)} sx={buttonSx}>
                                                    {`Plate ${plate}`}
                                                </Button>
                                            ))}
                                        </Stack>
                                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                                            {round.plates.map(({ plate, marking_time: markingTime }) => (
                                                <MarkingTimeChip
                                                    key={plate}
                                                    value={markingTime}
                                                    label={round.plates.length > 1 ? `Plate ${plate}` : "Predicted marking time"}
                                                />
                                            ))}
                                        </Stack>
                                        <LaserMarkPanel
                                            getMarkingBlob={() => fetchPlate(markPlate)}
                                            predictedMarkingTime={round.plates.find((entry) => entry.plate === markPlate)?.marking_time}
                                        >
                                            {round.plates.length > 1 && (
                                                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                                                    {round.plates.map(({ plate }) => (
                                                        <Chip key={plate} label={`Plate ${plate}`} color={plate === markPlate ? "primary" : "default"} onClick={() => setMarkPlate(plate)} />
                                                    ))}
                                                </Stack>
                                            )}
                                        </LaserMarkPanel>
                                    </Paper>
                                )}

                                {platesReady && (
                                    <Paper elevation={0} sx={cardSx}>
                                        <Typography variant="subtitle1" fontWeight={700}>Photos</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            One normalization image, then one ARW/NEF photo per set. The corner crosses are detected on the stored file.
                                        </Typography>
                                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mt: 2, p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="subtitle2" fontWeight={700}>Normalization image</Typography>
                                                    {round.normalization && <Chip size="small" color="success" label="Uploaded" />}
                                                </Stack>
                                                <CameraCapturePanel
                                                    label="Capture normalization photo"
                                                    disabled={norm.busy || measuring}
                                                    onCaptured={processNormFile}
                                                    manualUpload={uploadButton("Manual upload", norm.busy || measuring, processNormFile)}
                                                />
                                            </Box>
                                            <Box sx={{ width: 140, flexShrink: 0, aspectRatio: "1", position: "relative", borderRadius: 2, overflow: "hidden", border: "1px dashed", borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                {norm.busy ? <LinearProgress sx={{ width: "80%" }} /> : norm.preview ? (
                                                    <img src={norm.preview} alt="Normalization preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">Preview appears here</Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                                            {sets.map((s) => (
                                                <Chip
                                                    key={s.key}
                                                    label={`${setLabel(s.key)} · ${s.grid_size[0]} × ${s.grid_size[1]}`}
                                                    color={s.capture.complete ? "success" : "default"}
                                                    variant={active?.key === s.key ? "filled" : "outlined"}
                                                    onClick={() => setActiveSet(s.key)}
                                                    aria-pressed={active?.key === s.key}
                                                />
                                            ))}
                                        </Stack>
                                        {active && renderSlot(active)}
                                    </Paper>
                                )}

                                {results && (
                                    <Paper elevation={0} sx={cardSx}>
                                        <Typography variant="subtitle1" fontWeight={700}>Results</Typography>
                                        <Alert severity={results.verdict.predictable ? "success" : "warning"} sx={{ borderRadius: 2, my: 1.5 }}>
                                            {`${results.verdict.predictable ? "Predictable" : "Not predictable"}: mean ΔE00 ${fmt(results.verdict.mean)} (limit below ${fmt(results.limits.mean, 1)}), max ${fmt(results.verdict.max)} (limit ${fmt(results.limits.max, 1)}).`}
                                        </Alert>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1 }}>Per primary</Typography>
                                        {simpleTable("Per primary", [
                                            ["Primary"], ["Solid vs. exploration ΔE00", "right"], ["Mean ΔE00", "right"], ["Max ΔE00", "right"], ["Take out", "center"],
                                        ], primaries.map((p) => (
                                            <TableRow key={p.sample_id}>
                                                <TableCell>{samples[p.sample_id] ? <PickChip sample={samples[p.sample_id]} label={primaryLabel(p)} /> : primaryLabel(p)}</TableCell>
                                                {numberCell(fmt(solids[p.index]?.drift_deltaE))}
                                                {numberCell(fmt(byPrimary[p.index]?.mean))}
                                                {numberCell(fmt(byPrimary[p.index]?.max))}
                                                <TableCell align="center">
                                                    <Checkbox
                                                        size="small"
                                                        checked={takeOut.includes(p.sample_id)}
                                                        onChange={() => setTakeOut((prev) => toggled(prev, p.sample_id))}
                                                        inputProps={{ "aria-label": `Take out ${primaryLabel(p)}` }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )))}
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }}>Per mix ratio</Typography>
                                        {simpleTable("Per mix ratio", [["Mix ratio"], ["Patches", "right"], ["Mean ΔE00", "right"], ["Max ΔE00", "right"]],
                                            Object.entries(results.by_ratio || {}).sort(([a], [b]) => Number(a) - Number(b)).map(([ratio, row]) => (
                                                <TableRow key={ratio}>
                                                    <TableCell>{ratioLabel(ratio)}</TableCell>
                                                    {numberCell(row.n)}
                                                    {numberCell(fmt(row.mean))}
                                                    {numberCell(fmt(row.max))}
                                                </TableRow>
                                            )))}
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }}>Pairs with the largest error</Typography>
                                        {simpleTable("Pairs with the largest error", [["Pair"], ["Mean ΔE00", "right"], ["Max ΔE00", "right"]],
                                            worstPairs.map((row) => (
                                                <TableRow key={`${row.a}-${row.b}`}>
                                                    <TableCell>{pairLabel(row)}</TableCell>
                                                    {numberCell(fmt(row.mean))}
                                                    {numberCell(fmt(row.max))}
                                                </TableRow>
                                            )))}
                                    </Paper>
                                )}

                                {measured && (
                                    <Paper elevation={0} sx={cardSx}>
                                        <Typography variant="subtitle1" fontWeight={700}>Take out</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {`Primaries ticked in the table leave the selection for good. The selection then restarts at Width Groups as iteration ${iteration + 1}.`}
                                        </Typography>
                                        <Button
                                            variant="outlined" size="small" disableElevation sx={{ ...buttonSx, mt: 2 }}
                                            disabled={takeOut.length === 0 || takingOut || measuring}
                                            startIcon={takingOut ? <CircularProgress size={16} thickness={5} /> : null}
                                            onClick={() => setTakeOutOpen(true)}
                                        >
                                            Take out
                                        </Button>
                                    </Paper>
                                )}

                                {history.length > 1 && (
                                    <Paper elevation={0} sx={cardSx}>
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Iterations</Typography>
                                        {simpleTable("Iterations", [["Iteration"], ["Width group"], ["Primaries", "right"], ["Taken out", "right"], ["Result"], ["Mean ΔE00", "right"], ["Max ΔE00", "right"]],
                                            history.map((row) => (
                                                <TableRow key={row.iteration} selected={row.iteration === iteration}>
                                                    <TableCell>{row.iteration}</TableCell>
                                                    <TableCell>{row.width_group ? `${row.width_group} µm` : "—"}</TableCell>
                                                    {numberCell(row.primary_count ?? "—")}
                                                    {numberCell(row.taken_out.length)}
                                                    <TableCell>
                                                        {row.verdict ? (row.verdict.predictable ? "Predictable" : "Not predictable") : (STATUS_LABELS[row.round_status] || "—")}
                                                    </TableCell>
                                                    {numberCell(fmt(row.verdict?.mean))}
                                                    {numberCell(fmt(row.verdict?.max))}
                                                </TableRow>
                                            )))}
                                    </Paper>
                                )}

                                {platesReady && (
                                    <Typography variant="body2" color="text.secondary">
                                        {measured
                                            ? "Next finalizes the primaries of this iteration."
                                            : canMeasure
                                                ? (measuring ? "Measuring every set…" : "Next measures every set.")
                                                : `${missingSets} set${missingSets === 1 ? "" : "s"}${round.normalization ? "" : " and the normalization image"} still required before measuring.`}
                                    </Typography>
                                )}
                            </>
                        )}
                        <NavBarBottom
                            variant="embedded"
                            // The primaries are in the test: the earlier steps are locked.
                            hideReturnButton
                            prevPage={listRoute}
                            nextPage={stepRoute("overview", IMAGE_SELECTION)}
                            enableNextPage={!(canMeasure || measured) || measuring || preparing || takingOut}
                            nextLoading={measuring}
                            nextPageClickFunc={() => (measured ? setFinalizeOpen(true) : measure())}
                        />
                    </Stack>
                </ExplorationPageShell>
            </Box>
            <FinalizeDialog
                open={finalizeOpen}
                onClose={() => setFinalizeOpen(false)}
                onConfirm={finalize}
                confirming={finalizing}
                title="Finalize the primaries?"
                text={round ? `The ${primaries.length} primaries of iteration ${iteration} become the palette. The selection is then finished; this cannot be undone.` : ""}
            />
            <FinalizeDialog
                open={takeOutOpen}
                onClose={() => setTakeOutOpen(false)}
                onConfirm={confirmTakeOut}
                confirming={takingOut}
                confirmLabel="Take out"
                title={`Take out ${takeOut.length} ${takeOut.length === 1 ? "primary" : "primaries"}?`}
                text={`${takeOutNames.join(", ")} ${takeOut.length === 1 ? "leaves" : "leave"} the selection for good. The selection restarts at Width Groups as iteration ${iteration + 1}. This cannot be undone.`}
            />
            <Modal open={modalSet != null} onClose={closeModal}>
                <Box
                    sx={{
                        position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                        width: "85%", height: "88%", bgcolor: "background.paper", boxShadow: 24, p: 2,
                        borderRadius: 2, display: "flex", flexDirection: "column",
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
                            {modalSet ? `Corner points — ${setLabel(modalSet)}` : "Corner points"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {`${modalSlot?.cornerPoints?.length ?? 0} of 4 — click to place: top-left, top-right, bottom-left, bottom-right`}
                        </Typography>
                        <Button variant="outlined" size="small" disabled={autoCornersBusy} onClick={detectCorners} sx={{ textTransform: "none", borderRadius: 2 }}>
                            Automatic detection
                        </Button>
                        <Button variant="outlined" size="small" onClick={() => modalSet != null && updateSlot(modalSet, { cornerPoints: [] })} sx={{ textTransform: "none", borderRadius: 2 }}>
                            Clear corners
                        </Button>
                        <Button variant="contained" size="small" disableElevation onClick={closeModal} sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
                            Done
                        </Button>
                    </Stack>
                    {autoCornersBusy && <LinearProgress sx={{ mb: 1 }} />}
                    {modalSlot?.preview && (
                        <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
                            <img
                                src={modalSlot.preview}
                                ref={modalImageRef}
                                alt="Marked set full screen"
                                style={{ width: "100%", height: "100%", objectFit: "contain", cursor: "crosshair", display: "block" }}
                                onClick={handleModalImageClick}
                                onLoad={() => setLayoutTick((t) => t + 1)}
                            />
                            {modalSlot.cornerPoints.map((coordinate, index) => cornerDot(coordinate, modalImageRef.current, index))}
                        </Box>
                    )}
                </Box>
            </Modal>
        </>
    );
}
